import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { trackEvent } from '@/lib/analytics';
import { useToast } from '@/components/ui/use-toast';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, userData: any) => Promise<void>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
  resendVerificationEmail: (email: string) => Promise<void>;
  // NEW: returns a status so callers can react without duplicate toasts
  signUpWithStatus?: (email: string, password: string, userData: any) => Promise<{ isExistingUser: boolean }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    setLoading(true);
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (_event === 'SIGNED_IN') trackEvent('login');
      if (_event === 'SIGNED_IN') {
        try {
          const existing = typeof localStorage !== 'undefined' ? localStorage.getItem('trust_mode_enabled') : null;
          if (existing === null) {
            localStorage.setItem('trust_mode_enabled', 'true');
          }
        } catch {
          // ignore storage access issues
        }
      }
      // if (_event === 'SIGNED_OUT') trackEvent('logout'); // This was causing a race condition. Moved to signOut function.
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      toast({ title: "Login failed", description: error.message, variant: "destructive" });
      throw error;
    }
  };

  const signUpWithStatus = async (email: string, password: string, userData: any) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: userData, emailRedirectTo: `${window.location.origin}/auth` },
    });
    if (error) {
      // Let caller handle UI; still throw so page can show error toast
      throw error;
    }

    const identitiesCount = data.user?.identities?.length ?? 0;
    const isExistingUser = identitiesCount === 0;
    return { isExistingUser };
  };

  const signUp = async (email: string, password: string, userData: any) => {
    try {
      const { isExistingUser } = await signUpWithStatus(email, password, userData);
      if (isExistingUser) {
        toast({
          title: "Email already registered",
          description:
            "This email is already associated with an account. We've sent you a sign-in/verification email. If you forgot your password, use 'Forgot password'.",
        });
      } else {
        toast({ title: "Account created", description: "Check your email to verify your account." });
      }
    } catch (error: any) {
      toast({ title: "Signup failed", description: error.message, variant: "destructive" });
      throw error;
    }
  };

  const signOut = async () => {
    try {
      // Track logout event first, but don't let it fail the entire logout process
      await trackEvent('logout');
    } catch (analyticsError) {
      // Silently catch analytics errors during logout
      console.warn('Failed to track logout event:', analyticsError);
    }
    
    // Use local scope to avoid 403 on global revocation in production
    const { error } = await supabase.auth.signOut({ scope: 'local' });
    if (error) {
      toast({ title: "Sign out failed", description: error.message, variant: "destructive" });
      throw error;
    }
  };

  const refreshSession = async () => {
    const { data, error } = await supabase.auth.refreshSession();
    if (error) {
      console.error("Error refreshing session:", error);
    } else {
      setSession(data.session);
      setUser(data.user);
    }
  };

  const resendVerificationEmail = async (email: string) => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: { emailRedirectTo: `${window.location.origin}/auth` },
    });
    if (error) {
      toast({ title: "Resend failed", description: error.message, variant: "destructive" });
      throw error;
    }
    toast({ title: "Verification email sent", description: "Check your inbox for the link." });
  };

  const value = { user, session, loading, signIn, signUp, signOut, refreshSession, resendVerificationEmail, signUpWithStatus };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};